if (!customElements.get('cinoton-community-videos')) {
  class CinotonCommunityVideos extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-community-rail]');
      this.cards = [...this.querySelectorAll('[data-community-card]')];
      if (!this.rail || !this.cards.length) return;

      this.prev = this.querySelector('[data-community-prev]');
      this.next = this.querySelector('[data-community-next]');
      this.counter = this.querySelector('[data-community-current]');
      this.prev?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.rail.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });
      this.bindDrag();
      this.bindVideos();
      this.updateNavigation();
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateNavigation();
      });
    }

    cardStep() {
      if (this.cards.length < 2) return this.cards[0]?.getBoundingClientRect().width || 0;
      return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
    }

    move(direction) {
      this.rail.scrollBy({ left: direction * this.cardStep(), behavior: 'smooth' });
    }

    updateNavigation() {
      const max = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const step = this.cardStep() || 1;
      const index = Math.max(0, Math.min(this.cards.length - 1, Math.round(this.rail.scrollLeft / step)));
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
      if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft >= max - 2;
    }

    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;
      this.rail.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.target.closest('a, button')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = this.rail.scrollLeft;
        this.rail.classList.add('is-dragging');
        this.rail.setPointerCapture(pointerId);
      });
      this.rail.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        this.rail.scrollLeft = startScroll - (event.clientX - startX);
      });
      const finish = (event) => {
        if (pointerId !== event.pointerId) return;
        this.rail.classList.remove('is-dragging');
        const step = this.cardStep();
        if (step) this.rail.scrollTo({ left: Math.round(this.rail.scrollLeft / step) * step, behavior: 'smooth' });
        pointerId = null;
      };
      this.rail.addEventListener('pointerup', finish);
      this.rail.addEventListener('pointercancel', finish);
    }

    bindVideos() {
      const autoplay = this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hydrateVideo = (video) => {
        if (!video || video.dataset.hydrated === 'true') return;
        if (video.dataset.poster) {
          video.poster = video.dataset.poster;
          delete video.dataset.poster;
        }
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          delete source.dataset.src;
        });
        video.dataset.hydrated = 'true';
        video.load();
      };
      this.cards.forEach((card) => {
        const video = card.querySelector('.cinoton-community-videos__video');
        const toggle = card.querySelector('[data-community-toggle]');
        if (!video || !toggle) return;
        video.autoplay = false;
        video.preload = 'none';
        toggle.dataset.paused = 'true';
        toggle.setAttribute('aria-label', 'Play video');
        toggle.addEventListener('click', () => {
          hydrateVideo(video);
          if (video.paused) {
            video.play().catch(() => {});
            toggle.dataset.paused = 'false';
            toggle.setAttribute('aria-label', 'Pause video');
            card.dataset.userPaused = 'false';
          } else {
            video.pause();
            toggle.dataset.paused = 'true';
            toggle.setAttribute('aria-label', 'Play video');
            card.dataset.userPaused = 'true';
          }
        });
      });

      if (!('IntersectionObserver' in window)) {
        this.cards.forEach((card) => hydrateVideo(card.querySelector('.cinoton-community-videos__video')));
        return;
      }
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('.cinoton-community-videos__video');
          const toggle = entry.target.querySelector('[data-community-toggle]');
          if (!video || !toggle) return;
          if (entry.isIntersecting) hydrateVideo(video);
          if (entry.isIntersecting && autoplay && entry.target.dataset.userPaused !== 'true') {
            video.play().then(() => {
              toggle.dataset.paused = 'false';
              toggle.setAttribute('aria-label', 'Pause video');
            }).catch(() => {
              toggle.dataset.paused = 'true';
              toggle.setAttribute('aria-label', 'Play video');
            });
          } else if (!entry.isIntersecting) {
            video.pause();
            toggle.dataset.paused = 'true';
            toggle.setAttribute('aria-label', 'Play video');
          }
        });
      }, { root: null, rootMargin: '300px 0px', threshold: .2 });
      this.cards.forEach((card) => this.videoObserver.observe(card));
    }

    disconnectedCallback() {
      this.videoObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  }

  customElements.define('cinoton-community-videos', CinotonCommunityVideos);
}
